import { Activity, BarChart3, Camera, HardDrive, Images } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { TrackerStats, TrackerUser } from '@pms/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DataTable, type Column } from '@/components/ui/data-table';
import { SectionHeader } from '@/components/ui/section-header';
import { StatTile } from '@/components/ui/stat-tile';
import { CHART_TOOLTIP_STYLE } from '@/lib/chartTheme';
import { formatDate, formatDateTimeIST, formatRelativeTime } from '@/lib/dateFormat';
import { formatFileSize } from '@/lib/fileFormat';
import { formatCount } from './trackerConstants';

interface Props {
  stats: TrackerStats | undefined;
  users: TrackerUser[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onOpenScreenshots: (userId: string) => void;
}

export function TrackerOverview({ stats, users, loading, error, onRetry, onOpenScreenshots }: Props) {
  const online = users.filter((u) => u.online);
  // The tracker sends newest first; the chart reads left-to-right in time.
  const perDay = [...(stats?.perDay ?? [])].reverse();
  const value = (v: string) => (stats ? v : '—');

  const columns: Column<TrackerUser>[] = [
    {
      key: 'person',
      header: 'Person',
      cell: (u) => (
        <div>
          <p className="font-medium">{u.name || u.userId}</p>
          <p className="text-xs text-muted-foreground">{u.email}</p>
        </div>
      ),
    },
    { key: 'machine', header: 'Machine', className: 'hidden sm:table-cell', cell: (u) => u.machine ?? '—' },
    { key: 'today', header: 'Today', className: 'text-right tabular-nums', cell: (u) => formatCount(u.capturesToday) },
    {
      key: 'last',
      header: 'Last capture',
      cell: (u) => (
        <span className="text-xs" title={formatDateTimeIST(u.lastCaptureAt)}>
          {formatRelativeTime(u.lastCaptureAt) || 'Never'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      className: 'text-right',
      cell: (u) => (
        <Button
          size="icon"
          variant="ghost"
          onClick={() => onOpenScreenshots(u.userId)}
          aria-label={`Screenshots of ${u.name || u.userId}`}
          title="Screenshots"
        >
          <Camera className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatTile
          label="Working now"
          value={value(formatCount(stats?.online))}
          sub={`of ${formatCount(stats?.users)} people`}
          icon={Activity}
          tone="emerald"
        />
        <StatTile
          label="Captures today"
          value={value(formatCount(stats?.today.captures))}
          sub={`${formatCount(stats?.today.images)} screenshots · ${formatCount(stats?.today.activeUsers)} active`}
          icon={Camera}
          tone="sky"
        />
        <StatTile
          label="All time"
          value={value(formatCount(stats?.captures))}
          sub={`${formatCount(stats?.images)} screenshots`}
          icon={Images}
          tone="violet"
        />
        <StatTile
          label="Storage"
          value={value(formatFileSize(stats?.bytes))}
          sub={`${formatFileSize(stats?.today.bytes)} added today`}
          icon={HardDrive}
          tone="amber"
        />
      </div>

      <Card>
        <SectionHeader
          icon={<BarChart3 />}
          title="Captures per day"
          hint={perDay.length ? `Last ${perDay.length} days` : undefined}
        />
        <CardContent className="p-4">
          {loading && !stats ? (
            <div className="h-[240px] animate-pulse rounded-xl bg-muted/40" />
          ) : perDay.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">No captures recorded yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={perDay} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                  tickFormatter={(d) => formatDate(d as string).slice(0, 6)}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                  width={36}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={CHART_TOOLTIP_STYLE}
                  cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
                  labelFormatter={(d) => formatDate(d as string)}
                  formatter={(v) => [formatCount(Number(v)), 'Captures']}
                />
                <Bar dataKey="captures" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} maxBarSize={24} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <SectionHeader icon={<Activity />} title="Working right now" hint={`${formatCount(online.length)} online`} />
        <CardContent className="p-4">
          <DataTable
            columns={columns}
            rows={online}
            rowKey={(u) => u.userId}
            loading={loading && users.length === 0}
            loadingRows={3}
            error={error}
            onRetry={onRetry}
            empty="No one is tracking right now. A person appears here as soon as their tracker app sends a heartbeat."
          />
        </CardContent>
      </Card>
    </div>
  );
}
